import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { URL_PARAMS } from "@/constants/urlParams";
import { SortDirection, TableColumn } from "@/types/table/tableProps";

export const useSortSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasOrderBy = searchParams.has(URL_PARAMS.ORDER_BY);
  const hasDescending = searchParams.has(URL_PARAMS.DESCENDING);

  const orderBy = hasOrderBy
    ? searchParams.get(URL_PARAMS.ORDER_BY)!
    : undefined;
  const descending = hasDescending
    ? searchParams.get(URL_PARAMS.DESCENDING) === "true"
    : undefined;

  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);

    // New column or no existing sort - set to descending
    if (!orderBy || column !== orderBy) {
      newParams.set(URL_PARAMS.ORDER_BY, column);
      newParams.set(URL_PARAMS.DESCENDING, "true");
      setSearchParams(newParams);
      return;
    }

    // Same column, currently descending - switch to ascending
    if (descending === true) {
      newParams.set(URL_PARAMS.DESCENDING, "false");
      setSearchParams(newParams);
      return;
    }

    // Same column, currently ascending - remove sort
    newParams.delete(URL_PARAMS.ORDER_BY);
    newParams.delete(URL_PARAMS.DESCENDING);
    setSearchParams(newParams);
  };

  const direction = !orderBy
    ? null
    : descending === true
      ? SortDirection.DESC
      : SortDirection.ASC;

  return {
    descending,
    orderBy,
    direction,
    handleSort,
  };
};

/**
 * Hook that provides a sort handler for DataTable component.
 * This handler matches the DataTable's onSort prop signature.
 *
 * @template T - The type of data in the table
 * @returns A handler function that updates URL search params with sort state
 *
 * @example
 * ```tsx
 * const handleSort = useTableSortHandler<ClientType>();
 *
 * <DataTable
 *   onSort={handleSort}
 *   // ... other props
 * />
 * ```
 */
export function useTableSortHandler<T = unknown>() {
  const [searchParams, setSearchParams] = useSearchParams();

  return useCallback(
    (accessor: TableColumn<T>["accessor"], direction: SortDirection | null) => {
      const newParams = new URLSearchParams(searchParams);
      const accessorString =
        typeof accessor === "string" ? accessor : String(accessor);

      // Reset to page 1 when sorting changes
      newParams.set(URL_PARAMS.PAGE, "1");

      // No direction - remove sort params
      if (!direction) {
        newParams.delete(URL_PARAMS.ORDER_BY);
        newParams.delete(URL_PARAMS.DESCENDING);
        setSearchParams(newParams);
        return;
      }

      // Set sort params
      newParams.set(URL_PARAMS.ORDER_BY, accessorString);
      newParams.set(
        URL_PARAMS.DESCENDING,
        direction === SortDirection.DESC ? "true" : "false"
      );
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );
}
